<script setup lang="ts">
  import { getCurrentInstance, ref, watch } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import { control } from 'leaflet';
  import type { Map as LeafletMap } from 'leaflet';
  import { LMap, LTileLayer } from 'vue2-leaflet';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { roundCoord } from '@/domain/store-setup/location-step';
  import type { ILatLng } from '@/domain/store-setup/location-step';
  import { OSM_TILE_ATTRIBUTION, OSM_TILE_URL } from '@/lib/osm-tiles';

  /**
   * The pin confirmation map (plan §4.1, D5): the existing Leaflet/OSM map
   * with a fixed pin drawn over its centre; the owner pans the map under the
   * pin. Only the owner's moves are emitted (the view set from `center` /
   * `zoom` is not). It lives under `v-show`: `refresh()` (and a resize
   * observer where supported) re-measures it once it is visible.
   */
  /** Views closer than this (degrees) count as the same place: no setView. */
  const SAME_VIEW_EPSILON = 1e-6;

  const props = defineProps<{
    center: ILatLng;
    zoom: number;
    /** A pin to confirm: the fixed pin, the «¿Está bien el lugar?» card and «Usar mi ubicación». */
    overlay: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'move', view: { lat: number; lng: number; zoom: number }): void;
    (e: 'locate'): void;
    (e: 'tiles-failed'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string) => String(proxy.$t(key));

  // Read once: the view is driven imperatively afterwards (LMap would pan on every prop change).
  const initialCenter: [number, number] = [props.center.lat, props.center.lng];
  const initialZoom = props.zoom;
  const mapOptions = { zoomControl: false };
  const root = ref<HTMLElement | null>(null);
  const tilesFailed = ref(false);
  let map: LeafletMap | null = null;
  /** Set while the component itself changes the view, whose events must not reach the owner's pin. */
  let programmatic = false;

  function silently(action: (leafletMap: LeafletMap) => void) {
    if (!map) return;
    programmatic = true;
    try {
      action(map);
    } finally {
      programmatic = false;
    }
  }

  function isCurrentView(): boolean {
    if (!map) return true;
    const current = map.getCenter();
    return (
      map.getZoom() === props.zoom &&
      Math.abs(current.lat - props.center.lat) < SAME_VIEW_EPSILON &&
      Math.abs(current.lng - props.center.lng) < SAME_VIEW_EPSILON
    );
  }

  /** Moves the map to the `center`/`zoom` props (non-animated: its events fire synchronously and are muted). */
  function applyView() {
    if (isCurrentView()) return;
    silently((leafletMap) =>
      leafletMap.setView([props.center.lat, props.center.lng], props.zoom, {
        animate: false,
      })
    );
  }

  function onMoveEnd() {
    if (!map || programmatic) return;
    const center = map.getCenter().wrap();
    emit('move', {
      lat: roundCoord(center.lat),
      lng: roundCoord(center.lng),
      zoom: map.getZoom(),
    });
  }

  function onReady(leafletMap: LeafletMap) {
    map = leafletMap;
    control
      .zoom({
        position: 'bottomleft',
        zoomInTitle: tr('menuImport.viewer.zoomIn'),
        zoomOutTitle: tr('menuImport.viewer.zoomOut'),
      })
      .addTo(leafletMap);
    leafletMap.on('moveend', onMoveEnd);
    refresh();
    applyView();
  }

  /** Re-measures the container (it may have been hidden); keeps the centre. */
  function refresh() {
    silently((leafletMap) =>
      leafletMap.invalidateSize({ animate: false, pan: true })
    );
  }

  function onTileError() {
    if (tilesFailed.value) return;
    tilesFailed.value = true;
    emit('tiles-failed');
  }

  function onTileLoad() {
    tilesFailed.value = false;
  }

  watch(() => [props.center.lat, props.center.lng, props.zoom], applyView);
  useResizeObserver(root, () => refresh());

  defineExpose({ refresh });
</script>

<template>
  <div ref="root" class="v2-setup-pin-map">
    <LMap
      class="map-canvas"
      :center="initialCenter"
      :zoom="initialZoom"
      :options="mapOptions"
      @ready="onReady"
    >
      <LTileLayer
        :url="OSM_TILE_URL"
        :attribution="OSM_TILE_ATTRIBUTION"
        @tileerror="onTileError"
        @tileload="onTileLoad"
      />
    </LMap>
    <template v-if="overlay">
      <div class="map-help">
        <p class="help-title">{{ $t('storeSetup.location.mapTitle') }}</p>
        <p class="help-text">{{ $t('storeSetup.location.mapHelp') }}</p>
      </div>
      <span class="pin-shadow" aria-hidden="true" />
      <span class="pin" aria-hidden="true">
        <svg class="pin-icon" viewBox="0 0 40 40">
          <path
            class="pin-body"
            d="M20 39s-13-11.4-13-21.5a13 13 0 0 1 26 0C33 27.6 20 39 20 39z"
          />
          <circle class="pin-dot" cx="20" cy="17.5" r="5" />
        </svg>
      </span>
      <PosUiButton
        class="map-locate"
        color="neutral"
        variant="outline"
        icon="crosshairs-gps"
        @click="emit('locate')"
        >{{ $t('storeSetup.location.useMyLocation') }}</PosUiButton
      >
    </template>
    <p v-if="tilesFailed" class="map-failed" role="status">
      {{ $t('storeSetup.location.mapFailed') }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-pin-map {
    @apply tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-bg-neutral-100;
    // Its own stacking context: Leaflet's panes (z 400–1000) stay under the overlays.
    .map-canvas {
      @apply tw-absolute tw-inset-0 tw-z-0;
      isolation: isolate;
    }
    .map-help {
      @apply tw-pointer-events-none tw-absolute tw-left-3 tw-right-3 tw-top-3 tw-z-10 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2 tw-shadow-sm;
    }
    .help-title {
      @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .help-text {
      @apply tw-m-0 tw-text-xs tw-text-neutral-600;
    }
    // The pin's tip sits exactly on the map centre.
    .pin {
      @apply tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-z-10 tw-h-10 tw-w-10 tw--translate-x-1/2 tw--translate-y-full tw-text-red-600;
      filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3));
    }
    .pin-icon {
      @apply tw-block tw-h-10 tw-w-10;
    }
    .pin-body {
      fill: currentColor;
    }
    .pin-dot {
      @apply tw-fill-white;
    }
    .pin-shadow {
      @apply tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-z-10 tw-h-1.5 tw-w-3.5 tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-full tw-bg-black/25;
    }
    .map-locate {
      @apply tw-absolute tw-bottom-8 tw-right-3 tw-z-10 tw-shadow-sm;
    }
    .map-failed {
      @apply tw-absolute tw-inset-x-3 tw-bottom-20 tw-z-10 tw-m-0 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-center tw-text-sm tw-text-neutral-700 tw-shadow-sm;
    }
    // Touch-size zoom buttons (Leaflet draws 30 px ones).
    // Neutral like every V2 control: the admin's global link colour is the brand orange.
    :deep(.leaflet-control-zoom a) {
      @apply tw-h-11 tw-w-11 tw-text-xl tw-text-neutral-900;
      line-height: 2.75rem;
    }
    :deep(.leaflet-control-attribution a) {
      @apply tw-text-neutral-600;
    }
  }
</style>
